import { useState, useEffect, useCallback } from 'react';
import axios from '@/utils/axiosConfig';
import { getApiUrl } from '@/utils/apiUrl';

let _cachedPermissions = null;
let _cacheTimestamp = 0;
const CACHE_TTL = 60000; // 1 minute

/**
 * Hook to check user permissions for RBAC.
 * Usage: const { can, permissions, loading } = usePermissions();
 *        if (can('clients.delete')) { ... }
 */
export function usePermissions() {
  const [permissions, setPermissions] = useState(_cachedPermissions || []);
  const [loading, setLoading] = useState(!_cachedPermissions);
  const API = getApiUrl();

  useEffect(() => {
    const now = Date.now();
    if (_cachedPermissions && (now - _cacheTimestamp) < CACHE_TTL) {
      setPermissions(_cachedPermissions);
      setLoading(false);
      return;
    }

    const token = sessionStorage.getItem('token');
    if (!token) { setLoading(false); return; }

    const fetchPerms = async () => {
      try {
        const res = await axios.get(`${API}/my-permissions`, { headers: { Authorization: `Bearer ${token}` } });
        const perms = res.data?.permissions || [];
        _cachedPermissions = perms;
        _cacheTimestamp = Date.now();
        setPermissions(perms);
      } catch (e) { void 0; }
      finally { setLoading(false); }
    };
    fetchPerms();
  }, [API]);

  const can = useCallback((permission) => {
    if (!permissions || permissions.length === 0) return true; // No RBAC = allow all (backward compat)
    if (permissions.includes('all')) return true;
    return permissions.includes(permission);
  }, [permissions]);

  return { can, permissions, loading };
}

/**
 * Clear permissions cache (call on login/logout/role change).
 */
export function clearPermissionsCache() {
  _cachedPermissions = null;
  _cacheTimestamp = 0;
}
